import React, { FC, useEffect, useState } from 'react';
import { Col, Form, Row, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import PinkBox from 'src/components/pink-box/pink-box';
import { i18nKey } from 'src/locales/i18n';
import FileUploader from 'src/components/uploader/uploader';
import useStore from 'src/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { ICorporateOnboardingStore } from 'src/store/corporate/onboarding.store';
import { ICorporateLevelDocuments } from 'src/interfaces/accounts/company';
import LabelWithToolTip from 'src/components/label-with-tooltip/label-with-tooltip';

import { scrollPageToTop } from 'src/helpers/window';
import { DEFAULT_ALLOWED_OTHER_FILES } from 'src/constants';

const CorporateLevelBorrowerDocuments: FC = () => {
	const [saving, setSaving] = useState(false);
	const [t] = useTranslation();

	const onboardingStore: ICorporateOnboardingStore = useStore('corporateOnboardingStore');

	const onFileDownload = async (type: string, id: string | undefined) => {
		setSaving(true);
		await onboardingStore.downloadCompanyFile(type as keyof ICorporateLevelDocuments, id);
		setSaving(false);
	};

	useEffect(() => {
		scrollPageToTop();
	}, []);

	return (
		<>
			<PinkBox>
				<Spin spinning={saving}>
					<Form
						initialValues={{
							...onboardingStore.corporateLevelDocuments
						}}>
						<Row>
							<Col span={24}>
								<Typography.Title level={4}>
									<LabelWithToolTip
										label={t(i18nKey.onboarding.request.companyDocuments.title)}
										tooltip={t(i18nKey.onboarding.request.companyDocuments.iconHint)}
									/>
								</Typography.Title>
							</Col>
						</Row>
						<Form.Item
							name="certificateOfIncorporation"
							rules={[
								{
									required: true,
									message: `${t(i18nKey.onboarding.validation.required)}`
								}
							]}>
							<FileUploader
								disabled
								onDownload={onFileDownload}
								typeId={'certificateOfIncorporation'}
								inputFiles={onboardingStore.corporateLevelDocuments?.certificateOfIncorporation || []}
								title={`${t(i18nKey.onboarding.request.companyDocuments.certificateOfIncorporation)}`}
							/>
						</Form.Item>

						<Form.Item
							name="businessRegistrationCertification"
							rules={[
								{
									required: true,
									message: `${t(i18nKey.onboarding.validation.required)}`
								}
							]}>
							<FileUploader
								typeId={'businessRegistrationCertification'}
								onDownload={onFileDownload}
								inputFiles={onboardingStore.corporateLevelDocuments?.businessRegistrationCertification || []}
								title={`${t(i18nKey.onboarding.request.companyDocuments.businessRegistrationCert)}`}
								disabled
							/>
						</Form.Item>

						<Form.Item name={'latestAnnualReturn'}>
							<FileUploader
								typeId={'latestAnnualReturn'}
								onDownload={onFileDownload}
								inputFiles={onboardingStore.corporateLevelDocuments?.latestAnnualReturn || []}
								title={`${t(i18nKey.onboarding.request.companyDocuments.latestAnnualReturn)}`}
								disabled
							/>
						</Form.Item>

						<Form.Item
							name={'latestAuditedFinancialStatement'}
							rules={[
								{
									required: true,
									message: `${t(i18nKey.onboarding.validation.required)}`
								}
							]}>
							<FileUploader
								typeId={'latestAuditedFinancialStatement'}
								onDownload={onFileDownload}
								inputFiles={onboardingStore.corporateLevelDocuments?.latestAuditedFinancialStatement || []}
								title={
									<div>
										<span>{t(i18nKey.onboarding.request.companyDocuments.latestAudited1)}</span>
										<br />
										<span>{t(i18nKey.onboarding.request.companyDocuments.latestAudited2)}</span>
									</div>
								}
								disabled
							/>
						</Form.Item>

						<Form.Item name="groupOwnership">
							<FileUploader
								typeId={'groupOwnership'}
								onDownload={onFileDownload}
								inputFiles={onboardingStore.corporateLevelDocuments?.groupOwnership || []}
								title={`${t(i18nKey.onboarding.request.companyDocuments.groupOwnership)}`}
								disabled
							/>
						</Form.Item>
						<Form.Item
							name={'signedBoardResolution'}
							rules={[
								{
									required: true,
									message: `${t(i18nKey.onboarding.validation.required)}`
								}
							]}>
							<FileUploader
								typeId={'signedBoardResolution'}
								onDownload={onFileDownload}
								inputFiles={onboardingStore.corporateLevelDocuments?.signedBoardResolution || []}
								title={
									<div>
										<span>{t(i18nKey.onboarding.request.companyDocuments.signedBoard)}</span>
										<br />
									</div>
								}
								disabled
							/>
						</Form.Item>
						<FileUploader
							typeId={'borrowerCorporateOthers'}
							onDownload={onFileDownload}
							inputFiles={onboardingStore.corporateLevelDocuments?.borrowerCorporateOthers || []}
							title={`${t(i18nKey.onboarding.request.companyDocuments.otherDocs)}`}
							accept={DEFAULT_ALLOWED_OTHER_FILES}
							disabled
						/>
					</Form>
				</Spin>
			</PinkBox>
		</>
	);
};

export default observer(CorporateLevelBorrowerDocuments);
